import { defineStore } from 'pinia';
import {
	query,
	orderBy,
	limit,
	startAfter,
	getDocsFromServer,
	getCountFromServer,
	where,
	onSnapshot,
	Timestamp,
	collectionGroup,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuthStore } from '../../../store/shared/auth-store';
import Conversation from '../entities/chats/conversation';
import { useTableStore } from '../../../store/shared/table-store';
import { useChatsStore } from './chats';
import { useConversationStore } from './conversation';
import { useLabelStore } from '@/v2/modules/whatsapp/stores/labels';
import { chanelMarketplace, channelTypes } from '../entities/channel';
import { hasMarketplace } from '@/v2/utils/marketplaces';

export const useChatsListStore = defineStore('whatsAppChatsListStore', {
	state: () => ({
		conversations: [],
		totalConversations: 0,
		unsubscribeFunction: undefined,
		selectedConversations: [],
		allSelected: false,
		newMessagesCount: 0,
		errorFetchingConversations: false,
		filters: {
			search: undefined,
			status: undefined,
			readingStatus: undefined,
			searchType: 'contact_display_name',
			label_ids: [],
			assigned_to_users: [],
			fromExpiry: undefined,
			toExpiry: undefined,
			escalation: false,
			aiDisabled: false,
			channelType: Object.values(channelTypes).filter((type) =>
				hasMarketplace(chanelMarketplace[type]),
			),
		},
		activeStatus: undefined,
		aiEscalationAlert: false,
		lastAppliedFilters: '',
		loading: false,
	}),
	getters: {},
	actions: {
		async fetchConversations({ per_page, filters }) {
			this.errorFetchingConversations = false;
			const authStore = useAuthStore();
			const tenantId = authStore.tenantId;
			const labelStore = useLabelStore();
			if (JSON.stringify(filters) !== this.lastAppliedFilters) {
				this.lastAppliedFilters = JSON.stringify(filters);
				this.conversations = [];
			}
			try {
				this.loading = true;
				if (this.unsubscribeFunction) {
					this.unsubscribeFunction();
					this.unsubscribeFunction = undefined;
				}

				const conversationsCollectionGroup = collectionGroup(db, 'conversations');

				const queryData = [
					conversationsCollectionGroup,
					where('tenantId', '==', `${tenantId}`),
				];
				if (filters.channelType.length) {
					queryData.push(where('channelType', 'in', filters.channelType));
				} else {
					queryData.push(where('channelType', 'in', ['#']));
				}

				// Filters

				const isExpirationFilterBounded = {
					from: false,
					to: false,
				};

				if (
					(filters.fromExpiry &&
						filters.toExpiry &&
						filters.fromExpiry > 0 &&
						filters.toExpiry <= 72 &&
						filters.fromExpiry <= filters.toExpiry) ||
					(filters.fromExpiry && filters.fromExpiry > 0 && filters.fromExpiry <= 72) ||
					(filters.toExpiry && filters.toExpiry > 0 && filters.toExpiry <= 72)
				) {
					if (filters.fromExpiry) {
						queryData.push(
							where(
								'conversation_expiration_timestamp',
								'>=',
								(
									Timestamp.fromDate(new Date())?.seconds +
									60 * 60 * filters.fromExpiry
								)?.toString(),
							),
						);
						isExpirationFilterBounded.from = true;
					}
					if (filters.toExpiry) {
						queryData.push(
							where(
								'conversation_expiration_timestamp',
								'<=',
								(
									Timestamp.fromDate(new Date())?.seconds +
									60 * 60 * filters.toExpiry
								)?.toString(),
							),
						);
						isExpirationFilterBounded.to = true;
					}
				}

				if (filters.readingStatus) {
					queryData.push(where('last_message.is_new', '==', filters.readingStatus));
				} else {
					queryData.push(where('last_message.is_new', 'in', [true, false]));
				}

				if (filters.status) {
					const condition = filters.status == 'open' ? '>' : '<=';
					if (filters.status == 'open') {
						isExpirationFilterBounded.from = true;
					} else {
						isExpirationFilterBounded.to = true;
					}
					queryData.push(
						where(
							'conversation_expiration_timestamp',
							condition,
							Timestamp.fromDate(new Date())?.seconds?.toString(),
						),
					);
				}

				if (!isExpirationFilterBounded.from) {
					queryData.push(where('conversation_expiration_timestamp', '>=', '0'));
				}
				if (!isExpirationFilterBounded.to) {
					queryData.push(
						where('conversation_expiration_timestamp', '<', '999999999999999999999999'),
					);
				}

				if (filters.label_ids.length) {
					const labelsArray = [];
					filters.label_ids.forEach((labelId) => {
						const label = labelStore.list.find((el) => el.id == labelId);

						if (labelId == '$FAILED_TEMPLATE$') {
							labelsArray.push({ id: '$FAILED_TEMPLATE$' });
						} else {
							labelsArray.push({
								id: labelId,
								color: label?.color_object?.hex_code?.primary,
							});
						}
					});
					if (filters.escalation) {
						labelsArray.push({ id: '$ESCALATED$' });
					}
					if (labelsArray.length) {
						queryData.push(where('labels', 'array-contains-any', labelsArray));
					} else {
						queryData.push(where('labels', 'array-contains', { id: '$DEFAULT$' }));
					}
				} else if (filters.escalation) {
					queryData.push(where('labels', 'array-contains', { id: '$ESCALATED$' }));
				} else {
					queryData.push(where('labels', 'array-contains', { id: '$DEFAULT$' }));
				}

				if (filters.assigned_to_users.length) {
					const assignedUsersIds = [];
					filters.assigned_to_users.forEach((el) => {
						assignedUsersIds.push(el?.id);
					});
					queryData.push(where('assigned_to_user.user_id', 'in', assignedUsersIds));
				}

				if (filters.contact_display_name) {
					let value = filters.contact_display_name;
					if (filters.searchType === 'whatsapp_id' && value.startsWith('+')) {
						value = value.slice(1);
					}
					queryData.push(where(filters.searchType, '>=', value));
					queryData.push(where(filters.searchType, '<=', value + '\uf8ff'));
				}

				if (filters.aiDisabled) {
					queryData.push(where('configs.aiEnabled', '>=', false));
					queryData.push(where('configs.aiEnabled', '<=', false));
				} else {
					queryData.push(where('configs.aiEnabled', '>=', false));
					queryData.push(where('configs.aiEnabled', '<=', true));
				}

				queryData.push(orderBy('timestamp', 'desc'));
				// console.log(queryData);
				// Get total count
				const totalRecordsQuery = query(...queryData);
				const total = (await getCountFromServer(totalRecordsQuery)).data().count;
				this.totalConversations = total;
				// if (total === 0) {
				// 	this.conversations = [];
				// 	// console.timeEnd('[WhatsApp V2] fetchConversations');
				// 	return {
				// 		data: [],
				// 		totalRecords: 0,
				// 	};
				// }

				const getCounter = per_page;

				const docsToListenCount = this.conversations.length + getCounter;
				// console.log({ docsToListenCount });
				const snapshotQuery = query(...queryData, limit(docsToListenCount)); // listen to first conversations.length items

				// Add limit and startAfter
				queryData.push(limit(getCounter));
				if (this.conversations.length) {
					queryData.push(startAfter(this.conversations[this.conversations.length - 1]));
				}

				const res = await getDocsFromServer(query(...queryData));
				// console.timeEnd('[WhatsApp V2] fetchConversations');
				this.conversations.push(...res.docs);
				const data = res.docs.map((doc) => {
					const conversation = new Conversation(doc.data());
					return conversation;
				});

				this.listenForChanges(snapshotQuery, totalRecordsQuery);

				return {
					data,
					totalRecords: total,
				};
			} catch (error) {
				this.errorFetchingConversations = true;
				console.error('[WhatsApp V2] error in Fetch Conversations', error);
				// throw error;
			} finally {
				this.loading = false;
			}
		},
		async syncTotalRecords(totalRecordsQuery) {
			const chatsListTableStore = useTableStore({
				tableName: 'chatsList',
				fetchDataFunction: this.fetchConversations,
			});
			const total = (await getCountFromServer(totalRecordsQuery)).data().count;
			this.totalConversations = total;
			chatsListTableStore.totalRecords = total;
		},
		listenForChanges(query, totalRecordsQuery) {
			const chatsListTableStore = useTableStore({
				tableName: 'chatsList',
				fetchDataFunction: this.fetchConversations,
			});
			try {
				const unsubscribe = onSnapshot(query, (querySnapshot) => {
					// console.log(querySnapshot);
					if (querySnapshot.metadata.fromCache) return;

					const docs = querySnapshot.docs;
					// console.log('[WhatsApp V2] New Snapshot From Conversations', querySnapshot, docs);
					this.conversations = docs;

					const data = docs.map((doc) => {
						const conversation = new Conversation(doc.data());
						return conversation;
					});
					chatsListTableStore.data = data;
					// console.log(chatsListTableStore.data);
					this.syncTotalRecords(totalRecordsQuery);
				});
				this.unsubscribeFunction = unsubscribe;
			} catch (error) {
				console.error('[WhatsApp V2] error in listenForChanges of Conversations', error);
			}
		},
		stopListening() {
			if (this.unsubscribeFunction) {
				this.unsubscribeFunction();
			}
		},
		watchNewConversations() {
			try {
				const authStore = useAuthStore();
				const tenantId = authStore.tenantId;

				const snapshotQuery = query(
					collectionGroup(db, 'conversations'),
					where('tenantId', '==', `${tenantId}`),
					where('last_message.is_new', '==', true),
					where('last_message.sent_by_merchant', '==', false),
				);

				onSnapshot(
					snapshotQuery,
					(snapshot) => {
						this.newMessagesCount = snapshot.size;
					},
					(error) => {
						console.error('Error monitoring conversations:', error);
					},
				);
			} catch (e) {
				console.error('watchNewConversations:', e);
			}
		},
		watchAiEscalation() {
			try {
				const authStore = useAuthStore();
				const tenantId = authStore.tenantId;

				const snapshotQuery = query(
					collectionGroup(db, 'conversations'),
					where('tenantId', '==', `${tenantId}`),
					where('configs.escalationText', '!=', null),
				);

				onSnapshot(
					snapshotQuery,
					(snapshot) => {
						this.aiEscalationAlert = snapshot.size > 0;
					},
					(error) => {
						console.error('Error monitoring conversations for escalation:', error);
					},
				);
			} catch (e) {
				console.error('watchAiEscalation:', e);
			}
		},
	},
});
